// TanStack Query hooks, one per endpoint the UI reads.
//
// Query keys are built from the SAME params object that becomes the query
// string, so two screens asking for the same page of deals share a cache entry
// and a filter change is a new key by construction — no hand-maintained key
// lists to fall out of step with the request.

import { useQueries, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  Agreement,
  AgreementWithTerms,
  BillRunReport,
  BillingLineupReport,
  CalendarReport,
  Deal,
  DealFact,
  DealListItem,
  DealStatement,
  FxRate,
  ImplementationFeeReport,
  listEnvelope,
  MonthCloseReport,
  PrepaidEntitlement,
  ProviderCostFact,
  ProviderRate,
  InvoiceLineListItem,
  InvoiceListItem,
  InvoiceStatus,
  Note,
  NoteListItem,
  NoteSearchResults,
  Partner,
  PartnerRollupReport,
  PipelineReport,
  ProjectionReport,
  DealHistory,
  SalesReport,
  ClosingPace,
  ProjectionSettings,
  RevenueByRegionReport,
  RunwayReport,
  SearchResults,
  TrendsReport,
  MarginReport,
  MarginTrendReport,
  BillingContact,
  TenantBinding,
  TermLine,
  TermLineRevision,
  UsageCostPending,
  UsageCostResponse,
  UsageJob,
  UsageTrendResponse,
} from "@crm/shared";
import type {
  CalendarEventKind,
  CountryCode,
  Currency,
  DealState,
  ListEnvelope,
  ReportScope,
  RevenueByRegionGroupBy,
  SortSpec,
  UsageCostGroupBy,
  UsageTrendGroupBy,
} from "@crm/shared";
import { apiGet, apiGetRaw, lenientParse } from "./client.js";
import type { UsageCostAnswer } from "../pages/usage/usageMerge.js";
import { assertNever } from "../lib/assertNever.js";
import { todayIsoDate } from "../format/calendarDate.js";
import type { QueryParams } from "./queryString.js";
import { serializeSorts } from "./queryString.js";
import {
  projectionQueryParams,
  type ProjectionQueryState,
} from "../pages/projections/projectionModel.js";

const DealListEnvelope = listEnvelope(DealListItem);
const PartnerListEnvelope = listEnvelope(Partner);
const NoteListEnvelope = listEnvelope(NoteListItem);
const AgreementListEnvelope = listEnvelope(Agreement);
const TermLineListEnvelope = listEnvelope(TermLine);
const TermLineRevisionListEnvelope = listEnvelope(TermLineRevision);
const DealFactListEnvelope = listEnvelope(DealFact);
const ProviderCostFactListEnvelope = listEnvelope(ProviderCostFact);
const PrepaidEntitlementListEnvelope = listEnvelope(PrepaidEntitlement);
const FxRateListEnvelope = listEnvelope(FxRate);

/**
 * `GET /api/deals/{id}` answers with the same denormalised list item as the
 * collection (see `apiRegistry.ts`: deals declare no separate detail schema),
 * so the detail screen gets `partner_name`/`agreement_name` without a second
 * round trip. Declared here as the list item, not `Deal`, so that stays true.
 */
const DealDetail = DealListItem;

export const DEFAULT_PAGE_SIZE = 25;

/**
 * The API's list ceiling (§06). A read that wants "everything in this window"
 * rather than a page — the calendar's month, a deal's whole schedule — asks for
 * exactly this and then compares `items.length` against `total`, because the
 * API truncates silently and a screen that does not check is a screen that
 * draws an incomplete month without saying so.
 */
export const MAX_LIST_LIMIT = 500;

// ---------------------------------------------------------------------------
// Deals
// ---------------------------------------------------------------------------

/**
 * The deals list's complete query state. It lives in the URL (see the route's
 * `validateSearch`), so a filtered, sorted page is a linkable thing — which is
 * how one person hands another "the three USD deals in contracting".
 */
export interface DealsQueryState {
  q: string;
  states: readonly DealState[];
  /**
   * A partner AND every company it owns — `under_partner_id` on the wire, the
   * same family reading the reports default to. "Harris" almost always means
   * Harris and Cayenta together; the one-company reading is the partner page's.
   */
  partnerId: string | null;
  currency: Currency | null;
  /** The floor on `win_prob`, or null for any. */
  winProbMin: number | null;
  /** When the deal starts, as a window from today — see `DEAL_START_WINDOWS`. */
  start: DealStartWindow;
  /** When the deal renews, likewise — see `DEAL_RENEWAL_WINDOWS`. */
  renewal: DealRenewalWindow;
  /**
   * ISO-3166-1 alpha-2, or null for "any country". No longer a toolbar
   * control — the regions page is where a location is worked — but still
   * honoured from the URL so a saved link keeps its meaning.
   */
  country: CountryCode | null;
  location: DealLocationFilter;
  sort: SortSpec;
  page: number;
  pageSize: number;
}

/**
 * The two enrichment worklists, as ONE control rather than two independent
 * booleans.
 *
 * `has_location` and `has_coordinates` are separate filters on the wire, but
 * only three of their four combinations mean anything: a deal with coordinates
 * and no country cannot exist (the country rule and the CHECK both forbid it),
 * so exposing the pair as two checkboxes would offer a filter that always
 * returns nothing. An enum can't express the impossible combination, and it is
 * what the URL carries — see `dealsSearch.ts`.
 */
export const DEAL_LOCATION_FILTERS = ["any", "unlocated", "no_coordinates"] as const;
export type DealLocationFilter = (typeof DEAL_LOCATION_FILTERS)[number];

/**
 * The start-date windows the toolbar offers, each a `start_date_earliest`
 * range from today. Relative rather than absolute because the question is
 * always asked from now ("what starts in the next quarter"), and a saved link
 * should keep asking it from the day it is opened.
 */
export const DEAL_START_WINDOWS = ["any", "next_30", "next_90", "this_year", "next_year"] as const;
export type DealStartWindow = (typeof DEAL_START_WINDOWS)[number];

/** The renewal windows, as a `renewal_date` range from today. */
export const DEAL_RENEWAL_WINDOWS = ["any", "next_90", "next_180", "next_365"] as const;
export type DealRenewalWindow = (typeof DEAL_RENEWAL_WINDOWS)[number];

function isoDaysFrom(today: string, days: number): string {
  const date = new Date(`${today}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function startWindowParams(window: DealStartWindow, today: string): QueryParams {
  const year = today.slice(0, 4);
  switch (window) {
    case "any":
      return {};
    case "next_30":
      return { start_date_earliest_gte: today, start_date_earliest_lte: isoDaysFrom(today, 30) };
    case "next_90":
      return { start_date_earliest_gte: today, start_date_earliest_lte: isoDaysFrom(today, 90) };
    case "this_year":
      return { start_date_earliest_gte: `${year}-01-01`, start_date_earliest_lte: `${year}-12-31` };
    case "next_year": {
      const next = String(Number(year) + 1);
      return { start_date_earliest_gte: `${next}-01-01`, start_date_earliest_lte: `${next}-12-31` };
    }
    default:
      return assertNever(window, "deal start window");
  }
}

function renewalWindowParams(window: DealRenewalWindow, today: string): QueryParams {
  switch (window) {
    case "any":
      return {};
    case "next_90":
      return { renewal_date_gte: today, renewal_date_lte: isoDaysFrom(today, 90) };
    case "next_180":
      return { renewal_date_gte: today, renewal_date_lte: isoDaysFrom(today, 180) };
    case "next_365":
      return { renewal_date_gte: today, renewal_date_lte: isoDaysFrom(today, 365) };
    default:
      return assertNever(window, "deal renewal window");
  }
}

/**
 * `no_coordinates` is deliberately `has_location=true` AND
 * `has_coordinates=false`, not `has_coordinates=false` alone: the second would
 * also sweep in every deal nobody has placed at all, which is the OTHER
 * worklist and a much longer one. "Located but not plottable" is the list
 * somebody works through with a map open.
 */
function locationParams(filter: DealLocationFilter): QueryParams {
  switch (filter) {
    case "any":
      return {};
    case "unlocated":
      return { has_location: "false" };
    case "no_coordinates":
      return { has_location: "true", has_coordinates: "false" };
    default:
      return assertNever(filter, "deal location filter");
  }
}

export const DEFAULT_DEALS_QUERY: DealsQueryState = {
  q: "",
  states: [],
  partnerId: null,
  currency: null,
  winProbMin: null,
  start: "any",
  renewal: "any",
  country: null,
  location: "any",
  sort: { field: "start_date_earliest", direction: "asc" },
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

/**
 * `today` is a parameter so the relative windows are pinned by the caller —
 * the page reads the clock once, and a test can hand in a fixed day.
 */
export function dealsQueryParams(
  state: DealsQueryState,
  today: string = todayIsoDate(),
): QueryParams {
  return {
    q: state.q,
    // Repeatable filter: the API ORs the values, which is what a multi-select
    // state filter means.
    state: [...state.states],
    under_partner_id: state.partnerId,
    currency: state.currency,
    win_prob_gte: state.winProbMin,
    ...startWindowParams(state.start, today),
    ...renewalWindowParams(state.renewal, today),
    customer_country: state.country,
    ...locationParams(state.location),
    sort: serializeSorts([state.sort]),
    limit: state.pageSize,
    offset: (state.page - 1) * state.pageSize,
  };
}

export function useDeals(state: DealsQueryState): UseQueryResult<ListEnvelope<DealListItem>> {
  const params = dealsQueryParams(state);
  return useQuery({
    queryKey: ["deals", params],
    queryFn: ({ signal }) => apiGet(DealListEnvelope, "/deals", { params, signal }),
    // A filter change should not flash the empty state; keeping the previous
    // page visible while the next one loads is what makes typing in the search
    // box feel like filtering rather than reloading.
    placeholderData: (previous) => previous,
  });
}

/**
 * EVERY deal the filters match, in the URL's sort order — for the board, which
 * shows the pipeline as columns and has no page to show.
 *
 * Same filters and sort as `useDeals`, so the two views agree on which deals
 * are on screen; only the paging differs. The API caps a page at
 * `MAX_LIST_LIMIT`, so this walks the offsets until the envelope's `total` is
 * accounted for rather than quietly drawing the first 500 — a board missing
 * its last column of cards would look complete, which is the worst way for a
 * limit to show up. The cache key shares the list's `["deals"]` prefix, so a
 * deal write invalidates the board the same way it does the list.
 */
export function useAllDeals(state: DealsQueryState): UseQueryResult<ListEnvelope<DealListItem>> {
  const params = dealsQueryParams({ ...state, page: 1, pageSize: MAX_LIST_LIMIT });
  return useQuery({
    queryKey: ["deals", "all", params],
    queryFn: async ({ signal }) => {
      const items: DealListItem[] = [];
      let total = 0;
      for (let offset = 0; ; offset += MAX_LIST_LIMIT) {
        const page = await apiGet(DealListEnvelope, "/deals", {
          params: { ...params, offset },
          signal,
        });
        items.push(...page.items);
        total = page.total;
        if (page.items.length === 0 || items.length >= total) break;
      }
      return { items, total, limit: Math.max(items.length, 1), offset: 0 };
    },
    placeholderData: (previous) => previous,
  });
}

export function useDeal(dealId: string): UseQueryResult<DealListItem> {
  return useQuery({
    queryKey: ["deal", dealId],
    queryFn: ({ signal }) => apiGet(DealDetail, `/deals/${dealId}`, { signal }),
  });
}

/**
 * The clauses this deal bills under, in the order a person reads them.
 *
 * Inactive lines come back too: a retired clause is exactly what somebody
 * checking last year's invoice comes here for, and hiding it would make a
 * frozen invoice line point at nothing.
 */
export function useTermLines(dealId: string): UseQueryResult<ListEnvelope<TermLine>> {
  const params: QueryParams = { deal_id: dealId, sort: ["effective_from:asc"], limit: 200 };
  return useQuery({
    queryKey: ["term-lines", params],
    queryFn: ({ signal }) => apiGet(TermLineListEnvelope, "/term-lines", { params, signal }),
  });
}

/**
 * The active clauses of several deals at once, in ONE request.
 *
 * For the list screen's question about a deal it is not showing: whether a deal
 * with no agreement needs one. `deal_id` is repeatable and the API ORs the
 * values, so a page of deals costs one read rather than one per row — and a
 * page where every deal has an agreement asks nothing at all.
 */
export function useTermLinesForDeals(
  dealIds: readonly string[],
): UseQueryResult<ListEnvelope<TermLine>> {
  const params: QueryParams = { deal_id: [...dealIds], active: true, limit: 200 };
  return useQuery({
    queryKey: ["term-lines", params],
    queryFn: ({ signal }) => apiGet(TermLineListEnvelope, "/term-lines", { params, signal }),
    enabled: dealIds.length > 0,
  });
}

/**
 * One line's revisions, newest first. Append-only and hashed: an issued invoice
 * pins the revision it billed, so this is what says whether an edit could have
 * moved a document that already went out.
 */
export function useTermLineRevisions(
  termLineId: string,
): UseQueryResult<ListEnvelope<TermLineRevision>> {
  const params: QueryParams = {
    term_line_id: termLineId,
    sort: ["revision:desc"],
    limit: DEFAULT_PAGE_SIZE,
  };
  return useQuery({
    queryKey: ["term-line-revisions", params],
    queryFn: ({ signal }) =>
      apiGet(TermLineRevisionListEnvelope, "/term-line-revisions", { params, signal }),
  });
}

/**
 * The facts recorded against this deal, newest period first.
 *
 * Read per DEAL rather than per line so the section shows one chronological
 * picture: a fact for a period no line covers is a real situation worth seeing,
 * and it would be invisible nested under each line.
 */
export function useDealFacts(dealId: string): UseQueryResult<ListEnvelope<DealFact>> {
  const params: QueryParams = { deal_id: dealId, sort: ["period:desc"], limit: 200 };
  return useQuery({
    queryKey: ["deal-facts", params],
    queryFn: ({ signal }) => apiGet(DealFactListEnvelope, "/deal-facts", { params, signal }),
  });
}

/**
 * The facts that say a clause's milestones landed.
 *
 * A milestone reads as reached because ONE `deal_facts` row of kind
 * `milestone_reached` names it, and a fact has no PATCH — so taking the claim
 * back means deleting that row, and the row has to be found before it can be
 * deleted. Read per clause and only when somebody asks: the runway draws every
 * engagement at once, and a query per milestone would be a hundred requests
 * for a correction almost nobody makes.
 */
export function useMilestoneReachedFacts(
  termLineId: string,
  enabled: boolean,
): UseQueryResult<ListEnvelope<DealFact>> {
  const params: QueryParams = {
    term_line_id: termLineId,
    kind: "milestone_reached",
    sort: ["period:desc"],
    limit: 200,
  };
  return useQuery({
    queryKey: ["deal-facts", params],
    queryFn: ({ signal }) => apiGet(DealFactListEnvelope, "/deal-facts", { params, signal }),
    enabled,
  });
}

/**
 * What a provider says a day cost us, newest first.
 *
 * Read-only, and beside the rate card on purpose: this is the FIRST price arm —
 * a provider's own figure outranks our card — so the two belong on one screen.
 * A long history is the cost explorer's question rather than this screen's.
 */
export function useProviderCostFacts(): UseQueryResult<ListEnvelope<ProviderCostFact>> {
  const params: QueryParams = { sort: ["day:desc", "provider:asc"], limit: DEFAULT_PAGE_SIZE * 2 };
  return useQuery({
    queryKey: ["provider-cost-facts", params],
    queryFn: ({ signal }) =>
      apiGet(ProviderCostFactListEnvelope, "/provider-cost-facts", { params, signal }),
  });
}

/**
 * The pools a deal or a partner holds.
 *
 * One hook with a scope rather than two, because the two reads differ only in
 * which filter carries the id — and a pool bought on one deal may cover term
 * lines on another, which is why the partner scope exists at all.
 */
export interface PrepaidScope {
  readonly kind: "deal" | "partner";
  readonly id: string;
}

export function usePrepaidEntitlements(
  scope: PrepaidScope,
): UseQueryResult<ListEnvelope<PrepaidEntitlement>> {
  const params: QueryParams = {
    ...(scope.kind === "deal" ? { deal_id: scope.id } : { partner_id: scope.id }),
    sort: ["period_start:desc"],
    limit: 200,
  };
  return useQuery({
    queryKey: ["prepaid-entitlements", params],
    queryFn: ({ signal }) =>
      apiGet(PrepaidEntitlementListEnvelope, "/prepaid-entitlements", { params, signal }),
    enabled: scope.id !== "",
  });
}

// ---------------------------------------------------------------------------
// The statement: a deal's term lines interpreted over one period
// ---------------------------------------------------------------------------

/**
 * The statement, in whichever of its two arms answered.
 *
 * A 202 is not a failure and not an empty result: it means the usage rollup
 * does not cover the period, a job is filling it, and the previous answer sits
 * beside it. The status picks the schema here, exactly as `fetchUsageCost`
 * does, so no screen has to guess an arm from the shape of a body.
 */
export type StatementAnswer =
  | { readonly kind: "ready"; readonly statement: DealStatement }
  | { readonly kind: "pending"; readonly pending: UsageCostPending };

async function fetchStatement(
  dealId: string,
  period: string,
  signal: AbortSignal,
): Promise<StatementAnswer> {
  const path = `/deals/${dealId}/statement`;
  const { status, body } = await apiGetRaw(path, { params: { period }, signal });
  if (status === 202) {
    return { kind: "pending", pending: lenientParse(UsageCostPending, body, path) };
  }
  return { kind: "ready", statement: lenientParse(DealStatement, body, path) };
}

export function useDealStatement(dealId: string, period: string): UseQueryResult<StatementAnswer> {
  return useQuery({
    queryKey: ["deal-statement", dealId, period],
    queryFn: ({ signal }) => fetchStatement(dealId, period, signal),
    // Stepping through months should not flash the empty state; the previous
    // period stays on screen until the next one lands.
    placeholderData: (previous) => previous,
  });
}

// ---------------------------------------------------------------------------
// Partners and agreements
// ---------------------------------------------------------------------------

export interface PartnersQueryState {
  q: string;
  /**
   * The `depth=0` filter: the top of every family, with the companies they own
   * left out. A flat list of every partner and a list of relationships are two
   * different questions once partners own each other, and neither one answers
   * the other.
   */
  topLevelOnly: boolean;
  page: number;
  pageSize: number;
}

export const DEFAULT_PARTNERS_QUERY: PartnersQueryState = {
  q: "",
  topLevelOnly: false,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

export function usePartners(state: PartnersQueryState): UseQueryResult<ListEnvelope<Partner>> {
  const params: QueryParams = {
    q: state.q,
    // Null, not `false`: `depth=false` would reach the API as a filter value it
    // has no reading of, and an unfiltered list is what "all partners" means.
    depth: state.topLevelOnly ? 0 : null,
    sort: ["name:asc"],
    limit: state.pageSize,
    offset: (state.page - 1) * state.pageSize,
  };
  return useQuery({
    queryKey: ["partners", params],
    queryFn: ({ signal }) => apiGet(PartnerListEnvelope, "/partners", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

export function usePartner(partnerId: string): UseQueryResult<Partner> {
  return useQuery({
    queryKey: ["partner", partnerId],
    queryFn: ({ signal }) => apiGet(Partner, `/partners/${partnerId}`, { signal }),
  });
}

export function useAgreements(partnerId: string): UseQueryResult<ListEnvelope<Agreement>> {
  const params: QueryParams = { partner_id: partnerId, sort: ["name:asc"], limit: 200 };
  return useQuery({
    queryKey: ["agreements", params],
    queryFn: ({ signal }) => apiGet(AgreementListEnvelope, "/agreements", { params, signal }),
    // The deal form asks for agreements before a partner is chosen. Fetching
    // `?partner_id=` with nothing after it would be a request for every
    // agreement in the system, rendered as if it belonged to one partner.
    enabled: partnerId !== "",
  });
}

/**
 * The partners this one directly owns, for the "Owned companies" card.
 *
 * `hasChildren` is the parent's own `child_count`, so a partner whose record
 * already says it owns nothing never makes the request — an empty card is not
 * worth a round trip whose answer is on screen already.
 */
export function useChildPartners(
  partnerId: string,
  hasChildren: boolean,
): UseQueryResult<ListEnvelope<Partner>> {
  const params: QueryParams = { parent_id: partnerId, sort: ["name:asc"], limit: 200 };
  return useQuery({
    queryKey: ["partners", params],
    queryFn: ({ signal }) => apiGet(PartnerListEnvelope, "/partners", { params, signal }),
    enabled: hasChildren,
  });
}

/**
 * The agreements a deal under this partner may pin: the partner's own, plus
 * every owner's above it. The walk up the ownership chain is the API's
 * (`available_to_partner_id`), not this client's, so the picker cannot offer an
 * agreement the server would answer `reference_not_found` for.
 */
export function useAvailableAgreements(partnerId: string): UseQueryResult<ListEnvelope<Agreement>> {
  const params: QueryParams = {
    available_to_partner_id: partnerId,
    sort: ["name:asc"],
    limit: 200,
  };
  return useQuery({
    queryKey: ["agreements", params],
    queryFn: ({ signal }) => apiGet(AgreementListEnvelope, "/agreements", { params, signal }),
    // Same reason as `useAgreements`: an empty partner would ask for every
    // agreement in the system and render it as one partner's choices.
    enabled: partnerId !== "",
  });
}

/**
 * Every partner, for the deal form's partner picker. A CRM for 2-5 people has
 * tens of partners, not thousands, so one request beats a type-ahead that has
 * to explain why the partner you know exists isn't in the list yet.
 *
 * It is also what the ownership walks in `partnerTree.ts` read: the parent
 * picker's exclusions and the deal form's "this is what inherit resolves to"
 * both need the tree, and both get it from this one cached list.
 */
/**
 * Each partner's colour by id, for a row that carries only the partner's name.
 * The deals list item does not denormalise the colour — it is decoration, and
 * a column on every deals row for a dot would be the wrong trade — so the two
 * deals screens join it from the partner list they already fetch for the
 * toolbar's filter. Empty until that list arrives, which draws no dot rather
 * than a wrong one.
 */
export function usePartnerColors(): ReadonlyMap<string, string> {
  const partners = usePartnerOptions();
  return useMemo(() => {
    const colors = new Map<string, string>();
    for (const partner of partners.data?.items ?? []) {
      if (partner.color !== null) colors.set(partner.id, partner.color);
    }
    return colors;
  }, [partners.data]);
}

/**
 * Every deal, by customer, for a picker.
 *
 * Its own hook rather than `useAllDeals`: that one takes the deals SCREEN's
 * filter state, and a caller that only wants a list of names should not have to
 * fabricate a page, a sort and an empty filter set to get one.
 */
export function useDealOptions(): UseQueryResult<ListEnvelope<DealListItem>> {
  const params: QueryParams = { sort: ["customer_name:asc"], limit: 200 };
  return useQuery({
    queryKey: ["deals", params],
    queryFn: ({ signal }) => apiGet(DealListEnvelope, "/deals", { params, signal }),
  });
}

export function usePartnerOptions(): UseQueryResult<ListEnvelope<Partner>> {
  const params: QueryParams = { sort: ["name:asc"], limit: 200 };
  return useQuery({
    queryKey: ["partners", params],
    queryFn: ({ signal }) => apiGet(PartnerListEnvelope, "/partners", { params, signal }),
  });
}

/**
 * The term timeline only exists on the agreement DETAIL response
 * (`AgreementWithTerms`), so a partner with three agreements needs three
 * requests. `useQueries` keeps that a fixed-shape list of results the screen
 * can render one card per, instead of an N+1 hidden inside a loop of hooks.
 */
export function useAgreementDetails(
  agreementIds: readonly string[],
): UseQueryResult<AgreementWithTerms>[] {
  return useQueries({
    queries: agreementIds.map((id) => ({
      queryKey: ["agreement", id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        apiGet(AgreementWithTerms, `/agreements/${id}`, { signal }),
    })),
  });
}

// ---------------------------------------------------------------------------
// Usage and FX
// ---------------------------------------------------------------------------

/** The dated FX table (D6), newest first — the report footnotes' source of truth. */
export function useFxRates(): UseQueryResult<ListEnvelope<FxRate>> {
  const params: QueryParams = { sort: ["effective_date:desc"], limit: 200 };
  return useQuery({
    queryKey: ["fx-rates", params],
    queryFn: ({ signal }) => apiGet(FxRateListEnvelope, "/fx-rates", { params, signal }),
  });
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export function usePipelineReport(currency: Currency): UseQueryResult<PipelineReport> {
  const params: QueryParams = { currency };
  return useQuery({
    queryKey: ["report", "pipeline", params],
    queryFn: ({ signal }) => apiGet(PipelineReport, "/reports/pipeline", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

export function useBillingLineupReport(
  currency: Currency,
  weeks: number,
): UseQueryResult<BillingLineupReport> {
  const params: QueryParams = { currency, weeks };
  return useQuery({
    queryKey: ["report", "billing-lineup", params],
    queryFn: ({ signal }) =>
      apiGet(BillingLineupReport, "/reports/billing-lineup", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

/**
 * What to invoice next, and what is stopping you.
 *
 * `as_of` is not a parameter here on purpose: the report defaults it to today,
 * and threading a client-side "today" into the query key would make every
 * cached answer expire at midnight in whatever timezone the browser is in. A
 * screen that wants to preview a future run asks for it explicitly.
 */
export function useBillRunReport(
  currency: Currency,
  partnerId: string | null,
): UseQueryResult<BillRunReport> {
  const params: QueryParams = {
    currency,
    ...(partnerId === null ? {} : { partner_id: partnerId }),
  };
  return useQuery({
    queryKey: ["report", "bill-run", params],
    queryFn: ({ signal }) => apiGet(BillRunReport, "/reports/bill-run", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

/**
 * A month, and everything that bills for it.
 *
 * The period is REQUIRED and is the whole query key, unlike every other report
 * here whose window has a default: a close is about one named month, and a hook
 * that defaulted to "this month" would answer a different question on the first
 * of the month than on the last day of it.
 *
 * The lines and the per-currency totals are never converted: a close is worked
 * in the currency the document will be raised in. `currency` names only the
 * one display figure beside them, `totals_converted`, and the rate the report
 * echoes for it in `fx`.
 */
export function useMonthCloseReport(
  period: string,
  partnerId: string | null,
  currency: Currency,
): UseQueryResult<MonthCloseReport> {
  const params: QueryParams = {
    period,
    ...(partnerId === null ? {} : { partner_id: partnerId }),
    currency,
  };
  return useQuery({
    queryKey: ["report", "month-close", params],
    queryFn: ({ signal }) => apiGet(MonthCloseReport, "/reports/month-close", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

/**
 * Every implementation plan, and what is coming up next across all of them.
 *
 * `include_complete` is in the key because it is a different report rather than
 * a filtered rendering of one: a finished engagement contributes no events, so
 * the two answers differ in `engagements` and agree in `next_events`, and a
 * client that reused one for the other would show a plan list missing rows the
 * user had just asked for.
 */
export function useRunwayReport(
  partnerId: string | null,
  includeComplete: boolean,
): UseQueryResult<RunwayReport> {
  const params: QueryParams = {
    include_complete: includeComplete,
    ...(partnerId === null ? {} : { partner_id: partnerId }),
  };
  return useQuery({
    queryKey: ["report", "runway", params],
    queryFn: ({ signal }) => apiGet(RunwayReport, "/reports/runway", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

/**
 * One partner's figures, at either level of its family.
 *
 * `scope` is the whole question: `direct` counts only this company's own deals
 * and returns a line for each company beneath it as well, while `rolled_up`
 * returns one line for the topmost partner covering the entire family. So the
 * scope belongs in the query key — the two are different reports about the same
 * partner, not two renderings of one answer.
 */
export function usePartnerRollupReport(
  currency: Currency,
  partnerId: string,
  scope: ReportScope,
): UseQueryResult<PartnerRollupReport> {
  const params: QueryParams = { currency, partner_id: partnerId, scope };
  return useQuery({
    queryKey: ["report", "partner-rollup", params],
    queryFn: ({ signal }) =>
      apiGet(PartnerRollupReport, "/reports/partner-rollup", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

/**
 * The same billed money the partner rollup reports, cut by where the end
 * customer is.
 *
 * Every parameter is in the key for the reason `usePartnerRollupReport`'s scope
 * is: `group_by=region` is not a finer rendering of the country answer, it is a
 * different set of rows from a different GROUP BY, and caching the two together
 * would show one under the other's heading.
 */
export interface RegionQueryState {
  currency: Currency;
  groupBy: RevenueByRegionGroupBy;
  /** Null means every partner. */
  partnerId: string | null;
  scope: ReportScope;
}

export const DEFAULT_REGION_QUERY: RegionQueryState = {
  currency: "CAD",
  groupBy: "country",
  partnerId: null,
  scope: "rolled_up",
};

export function regionQueryParams(state: RegionQueryState): QueryParams {
  return {
    currency: state.currency,
    group_by: state.groupBy,
    partner_id: state.partnerId,
    // Meaningless without a partner — the report scopes nothing — so it is left
    // off rather than sent as a parameter that cannot change the answer.
    scope: state.partnerId === null ? null : state.scope,
  };
}

export function useRevenueByRegionReport(
  state: RegionQueryState,
): UseQueryResult<RevenueByRegionReport> {
  const params = regionQueryParams(state);
  return useQuery({
    queryKey: ["report", "revenue-by-region", params],
    queryFn: ({ signal }) =>
      apiGet(RevenueByRegionReport, "/reports/revenue-by-region", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

/** Which partner's deals the map shows. Same semantics the reports use. */
export interface MapScope {
  /** Null means every partner. */
  partnerId: string | null;
  scope: ReportScope;
}

/**
 * The deal states the map draws: everything but lost. Filtered on the wire so
 * lost deals do not use up the limit.
 */
const MAP_STATES = [
  "lead",
  "qualified",
  "demoed",
  "proposal",
  "decision_maker_bought_in",
  "won",
  "implementation",
  "live",
  "ended",
] as const satisfies readonly DealState[];

/**
 * The API's page ceiling. `total` comes back in the envelope, so the map can
 * say when more deals matched than it drew.
 */
export const MAP_DEAL_LIMIT = 500;

function mapScopeParams(scope: MapScope): QueryParams {
  if (scope.partnerId === null) return {};
  return scope.scope === "direct"
    ? { partner_id: scope.partnerId }
    : { under_partner_id: scope.partnerId };
}

/** Every non-lost deal with a coordinate. */
export function useMapDeals(scope: MapScope): UseQueryResult<ListEnvelope<DealListItem>> {
  const params: QueryParams = {
    has_coordinates: "true",
    state: [...MAP_STATES],
    ...mapScopeParams(scope),
    limit: MAP_DEAL_LIMIT,
  };
  return useQuery({
    queryKey: ["deals", "map", params],
    queryFn: ({ signal }) => apiGet(DealListEnvelope, "/deals", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

/**
 * How many non-lost deals the map cannot draw, per deals-list worklist:
 * `unlocated` (no country) and `no_coordinates` (a country but no point).
 * Only `total` is read, so one row is asked for.
 */
export function useMapLocationGap(
  scope: MapScope,
  filter: Exclude<DealLocationFilter, "any">,
): UseQueryResult<ListEnvelope<DealListItem>> {
  const params: QueryParams = {
    ...locationParams(filter),
    state: [...MAP_STATES],
    ...mapScopeParams(scope),
    limit: 1,
  };
  return useQuery({
    queryKey: ["deals", "map-gap", params],
    queryFn: ({ signal }) => apiGet(DealListEnvelope, "/deals", { params, signal }),
  });
}

/**
 * One deal's implementation-fee suggestion: what its buy-in fee should be under
 * the agreement that governs it, and what it currently bills.
 *
 * The report, filtered to a single deal, rather than an endpoint of its own. The
 * resolution — account count to band, band to fee, term to our share — has one
 * implementation, so the card on a deal screen and the reconciliation table
 * across every deal cannot come to different conclusions about the same deal.
 *
 * Reported in the DEAL's currency, not a fixed one. This card sits beside that
 * deal's term lines, which are all denominated in it (D8), and a suggestion
 * converted into some other currency would make the one number a reader is
 * about to type into a term line the one number on screen they cannot use.
 * The row still carries `tier_fee_minor` + `tier_currency`, so the figure as the
 * agreement actually writes it stays visible beside the converted one.
 */
export function useDealImplementationFee(
  dealId: string,
  currency: Currency,
): UseQueryResult<ImplementationFeeReport> {
  const params: QueryParams = { deal_id: dealId, currency };
  return useQuery({
    queryKey: ["report", "implementation-fee", params],
    queryFn: ({ signal }) =>
      apiGet(ImplementationFeeReport, "/reports/implementation-fee", { params, signal }),
  });
}

/**
 * Every dated commitment in a window: charges that fall due, costs to recover,
 * agreement terms that take effect, implementation fees, term-line windows that
 * open and close, deal starts and renewals.
 *
 * ONE request, not six list reads. The list endpoints cap at 500 rows each and
 * truncate silently, so a month assembled here would be a month that can be
 * wrong without saying so — and two of the seven kinds are resolutions rather
 * than rows (see the contract's `CalendarReport`). The report also converts, so
 * the screen can show one figure per day with a rate footnote under it instead
 * of refusing to add CAD to USD the way the billing calendar has to.
 *
 * The window is the grid's FULL visible range, leading and trailing days
 * included, for the same reason the billing calendar queries its own: a cell
 * that is drawn but not queried claims a day is empty when nobody asked.
 */
export interface CalendarQueryState {
  /** Inclusive window, normally a month grid's first and last cell. */
  from: string;
  to: string;
  currency: Currency;
  /** Empty means every kind, which is what the API does with no filter. */
  kinds: readonly CalendarEventKind[];
  /** Null means every partner. */
  partnerId: string | null;
  scope: ReportScope;
  /**
   * Drop bills and recharges that have already gone out. Optional and off by
   * default, which is what the calendar page wants: it is a record of dates, and
   * "what went out on the 30th" is a question worth being able to ask. The
   * billing home's "Coming up" band is the one caller that sets it, because a
   * band under that heading full of last month's issued invoices is not a
   * forecast.
   */
  unbilledOnly?: boolean;
}

export function calendarQueryParams(state: CalendarQueryState): QueryParams {
  return {
    from_date: state.from,
    to_date: state.to,
    currency: state.currency,
    // Repeatable filter: the API ORs the values, which is what the kind chips
    // mean. Empty sends nothing at all, which is "every kind".
    event_kind: [...state.kinds],
    partner_id: state.partnerId,
    // Meaningless without a partner — the report scopes nothing — so it is left
    // off rather than sent as a parameter that cannot change the answer.
    scope: state.partnerId === null ? null : state.scope,
    // Off is the API's default, so it is left off entirely rather than sent as
    // `false` — which keeps the query key (and the cache entry) identical to
    // what every existing caller already produces.
    unbilled_only: state.unbilledOnly === true ? true : null,
  };
}

export function useCalendarReport(state: CalendarQueryState): UseQueryResult<CalendarReport> {
  const params = calendarQueryParams(state);
  return useQuery({
    queryKey: ["report", "calendar", params],
    queryFn: ({ signal }) => apiGet(CalendarReport, "/reports/calendar", { params, signal }),
    // Paging a month at a time should not flash the empty state — the previous
    // month stays on screen until the next one lands.
    placeholderData: (previous) => previous,
  });
}

/** Re-exported so screens can type a row without reaching past the hook layer. */
export type { Deal };

// ---------------------------------------------------------------------------
// Invoices — the document, and what is on it.
// ---------------------------------------------------------------------------

export interface InvoiceQueryState {
  statuses: readonly InvoiceStatus[];
  partnerId: string | null;
  page: number;
  pageSize: number;
}

function invoiceParams(state: InvoiceQueryState): QueryParams {
  return {
    status: [...state.statuses],
    partner_id: state.partnerId,
    limit: state.pageSize,
    offset: (state.page - 1) * state.pageSize,
    sort: "due_date:asc",
  };
}

export function useInvoices(
  state: InvoiceQueryState,
): UseQueryResult<ListEnvelope<InvoiceListItem>> {
  const params = invoiceParams(state);
  return useQuery({
    queryKey: ["invoices", params],
    queryFn: ({ signal }) => apiGet(InvoiceListEnvelope, "/invoices", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

/**
 * One document. Its own key rather than a lookup into the list's cache, because
 * the sheet is reached by link as often as by clicking a row — and a screen that
 * only worked when you arrived from the list would be a screen you cannot send.
 */
export function useInvoice(id: string): UseQueryResult<InvoiceListItem> {
  return useQuery({
    queryKey: ["invoice", id],
    queryFn: ({ signal }) => apiGet(InvoiceListItem, `/invoices/${id}`, { signal }),
  });
}

/**
 * The lines of one document, in the order they are laid out.
 *
 * Unpaged at `MAX_LIST_LIMIT`: an invoice with more than five hundred lines is
 * not a thing, and paging a document would mean a total that covered a page.
 */
export function useInvoiceLines(
  invoiceId: string,
): UseQueryResult<ListEnvelope<InvoiceLineListItem>> {
  const params: QueryParams = {
    invoice_id: invoiceId,
    limit: MAX_LIST_LIMIT,
    sort: "sort_order:asc",
  };
  return useQuery({
    queryKey: ["invoice-lines", params],
    queryFn: ({ signal }) => apiGet(InvoiceLineListEnvelope, "/invoice-lines", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

const InvoiceListEnvelope = listEnvelope(InvoiceListItem);
const InvoiceLineListEnvelope = listEnvelope(InvoiceLineListItem);

// ---------------------------------------------------------------------------
// Trends
// ---------------------------------------------------------------------------

/** The three trailing windows the screen offers. */
export type TrendsWindowMonths = 12 | 24 | 36;

export interface TrendsQueryState {
  /** Trailing months, ending this month. */
  months: TrendsWindowMonths;
  currency: Currency;
  /** Null means every partner. */
  partnerId: string | null;
  scope: ReportScope;
}

export const DEFAULT_TRENDS_QUERY: TrendsQueryState = {
  months: 12,
  currency: "CAD",
  partnerId: null,
  scope: "rolled_up",
};

/**
 * `from_month` for a trailing window ending in `thisMonth` (`YYYY-MM`). The
 * report defaults `to_month` to the current month, so only the start is sent
 * — and it is sent rather than left to the report's own default because the
 * screen offers windows the report does not (24 and 36 months).
 */
export function trendsFromMonth(months: TrendsWindowMonths, thisMonth: string): string {
  const year = Number(thisMonth.slice(0, 4));
  const monthIndex = Number(thisMonth.slice(5, 7)) - 1 - (months - 1);
  const shiftedYear = year + Math.floor(monthIndex / 12);
  const shiftedMonth = ((monthIndex % 12) + 12) % 12;
  return `${String(shiftedYear).padStart(4, "0")}-${String(shiftedMonth + 1).padStart(2, "0")}`;
}

export function trendsQueryParams(state: TrendsQueryState, thisMonth: string): QueryParams {
  return {
    from_month: trendsFromMonth(state.months, thisMonth),
    currency: state.currency,
    partner_id: state.partnerId,
    // Meaningless without a partner — the report scopes nothing — so it is left
    // off rather than sent as a parameter that cannot change the answer.
    scope: state.partnerId === null ? null : state.scope,
  };
}

/**
 * `thisMonth` is a parameter rather than read from the clock here, so the
 * query key is stable for the life of a page and a test can pin it. The page
 * reads it once on mount.
 */
export function useTrendsReport(
  state: TrendsQueryState,
  thisMonth: string,
): UseQueryResult<TrendsReport> {
  const params = trendsQueryParams(state, thisMonth);
  return useQuery({
    queryKey: ["report", "trends", params],
    queryFn: ({ signal }) => apiGet(TrendsReport, "/reports/trends", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

// ---------------------------------------------------------------------------
// Projections
// ---------------------------------------------------------------------------

/**
 * Revenue month by month with actual, contracted and estimated money apart.
 * The scenario overrides ride in the params, so each what-if is its own cache
 * entry and flipping back to an earlier one is instant.
 */
export function useProjectionReport(state: ProjectionQueryState): UseQueryResult<ProjectionReport> {
  const params = projectionQueryParams(state);
  return useQuery({
    queryKey: ["report", "projection", params],
    queryFn: ({ signal }) => apiGet(ProjectionReport, "/reports/projection", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

/** What `/api/reports/sales` is asked: the window, how it is cut, and whose deals. */
export interface SalesQuery {
  readonly from: string | null;
  readonly to: string | null;
  readonly bucket: "week" | "month";
  readonly partnerId: string | null;
  readonly scope: "rolled_up" | "direct";
  readonly currency: string;
  readonly coldDays: number;
}

/**
 * The sales pipeline over time, from the deals' own estimates. Cheap to answer
 * (it never reads usage), so every control change is its own request.
 */
export function useSalesReport(query: SalesQuery): UseQueryResult<SalesReport> {
  const params: QueryParams = {
    from: query.from,
    to: query.to,
    bucket: query.bucket,
    partner_id: query.partnerId,
    scope: query.partnerId === null ? null : query.scope,
    currency: query.currency,
    cold_days: query.coldDays,
  };
  return useQuery({
    queryKey: ["report", "sales", params],
    queryFn: ({ signal }) => apiGet(SalesReport, "/reports/sales", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

/** How fast deals have closed lately, and the go-live capacity and growth that would match it. */
export function useClosingPace(): UseQueryResult<ClosingPace> {
  return useQuery({
    queryKey: ["report", "closing-pace"],
    queryFn: ({ signal }) => apiGet(ClosingPace, "/reports/closing-pace", { signal }),
  });
}

/** Everything recorded about one deal over time: its stages, imported ones included, and its edits. */
export function useDealHistory(dealId: string): UseQueryResult<DealHistory> {
  return useQuery({
    queryKey: ["deal-history", dealId],
    queryFn: ({ signal }) => apiGet(DealHistory, `/deals/${dealId}/history`, { signal }),
  });
}

/** The one stored set of projection assumptions, with the version a PATCH must name. */
export function useProjectionSettings(): UseQueryResult<ProjectionSettings> {
  return useQuery({
    queryKey: ["projection-settings"],
    queryFn: ({ signal }) => apiGet(ProjectionSettings, "/projection-settings", { signal }),
  });
}

// ---------------------------------------------------------------------------
// Margin
// ---------------------------------------------------------------------------

/** One month's margin, customer by customer, converted to `currency`. */
export function useMarginReport(period: string, currency: Currency): UseQueryResult<MarginReport> {
  const params: QueryParams = { period, currency };
  return useQuery({
    queryKey: ["report", "margin", params],
    queryFn: ({ signal }) => apiGet(MarginReport, "/reports/margin", { params, signal }),
    placeholderData: (previous) => previous,
  });
}

export interface MarginTrendQuery {
  /** The window's last month, `YYYY-MM`. */
  toMonth: string;
  months: TrendsWindowMonths;
  currency: Currency;
  /** Null means every partner. */
  partnerId: string | null;
  scope: ReportScope;
}

/**
 * Margin month by month over a trailing window ending in `toMonth`.
 *
 * `enabled: false` holds the request back (the Trends page waits for its own
 * report first: both work out the open month's statements, and asking for
 * them side by side doubles the server's work at the moment it is slowest).
 */
export function useMarginTrend(
  query: MarginTrendQuery,
  options: { enabled?: boolean } = {},
): UseQueryResult<MarginTrendReport> {
  return useMarginTrendRange(
    {
      fromMonth: trendsFromMonth(query.months, query.toMonth),
      toMonth: query.toMonth,
      currency: query.currency,
      partnerId: query.partnerId,
      scope: query.scope,
    },
    options,
  );
}

export interface MarginTrendRange {
  fromMonth: string;
  toMonth: string;
  currency: Currency;
  /** Null means every partner. */
  partnerId: string | null;
  scope: ReportScope;
}

/**
 * Margin month by month over any window. The margin page asks for the months
 * BEFORE the one it shows and adds that month from its own month report
 * (`pages/margin/marginHistory.ts`), so the server works the shown month out
 * once rather than twice.
 */
export function useMarginTrendRange(
  range: MarginTrendRange,
  options: { enabled?: boolean } = {},
): UseQueryResult<MarginTrendReport> {
  const params: QueryParams = {
    from_month: range.fromMonth,
    to_month: range.toMonth,
    currency: range.currency,
    partner_id: range.partnerId,
    scope: range.partnerId === null ? null : range.scope,
  };
  return useQuery({
    queryKey: ["report", "margin-trend", params],
    queryFn: ({ signal }) => apiGet(MarginTrendReport, "/reports/margin-trend", { params, signal }),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });
}

// ---------------------------------------------------------------------------
// Usage cost — the cost explorer (docs/crm-cost-explorer.md)
// ---------------------------------------------------------------------------

const BillingContactListEnvelope = listEnvelope(BillingContact);

/** Whose own contacts to read: a partner's or a deal's, never the resolved list. */
export type BillingContactOwner = { kind: "partner"; id: string } | { kind: "deal"; id: string };

/**
 * The people one partner or one deal holds itself. Who a deal's invoices
 * actually go to is `effective_billing_contacts` on the deal, which also
 * carries its partner's people for the roles the deal names nobody in.
 */
export function useBillingContacts(
  owner: BillingContactOwner,
): UseQueryResult<ListEnvelope<BillingContact>> {
  const params: QueryParams = {
    ...(owner.kind === "partner" ? { partner_id: owner.id } : { deal_id: owner.id }),
    limit: MAX_LIST_LIMIT,
  };
  return useQuery({
    queryKey: ["billing-contacts", params],
    queryFn: ({ signal }) =>
      apiGet(BillingContactListEnvelope, "/billing-contacts", { params, signal }),
  });
}

const TenantBindingListEnvelope = listEnvelope(TenantBinding);

/**
 * The (deployment, tenant) pairs a deal is scoped by.
 *
 * The deal's own screens read the BINDINGS rather than the facts, because a
 * binding is what says a tenant's spending belongs to this deal — and a binding
 * that has ended is what keeps a tenant that moved between deals inside the
 * deal it was bound to at the time.
 */
export function useTenantBindings(dealId: string): UseQueryResult<ListEnvelope<TenantBinding>> {
  const params: QueryParams = { deal_id: dealId, limit: MAX_LIST_LIMIT };
  return useQuery({
    queryKey: ["tenant-bindings", params],
    queryFn: ({ signal }) =>
      apiGet(TenantBindingListEnvelope, "/tenant-bindings", { params, signal }),
  });
}

/** Every binding in the CRM: what `/usage` subtracts to find the tenants no deal covers. */
export function useAllTenantBindings(): UseQueryResult<ListEnvelope<TenantBinding>> {
  const params: QueryParams = { limit: MAX_LIST_LIMIT };
  return useQuery({
    queryKey: ["tenant-bindings", params],
    queryFn: ({ signal }) =>
      apiGet(TenantBindingListEnvelope, "/tenant-bindings", { params, signal }),
  });
}

/** One read of the usage-cost report: one deployment, one tenant, one grouping. */
export interface UsageCostQuery {
  readonly period: string;
  readonly groupBy: UsageCostGroupBy;
  /** Null asks fleet-wide, which only `/usage` does. */
  readonly deploymentSlug: string | null;
  readonly tenantSlug: string | null;
  /**
   * What every figure comes back in. Named by every caller rather than
   * defaulted: a screen across deals passes the reader's report currency, and a
   * line on one deal passes that deal's, because its figure is printed in it.
   */
  readonly currency: Currency;
}

function usageCostParams(query: UsageCostQuery): QueryParams {
  return {
    period: query.period,
    group_by: query.groupBy,
    currency: query.currency,
    ...(query.deploymentSlug === null ? {} : { deployment_slug: query.deploymentSlug }),
    ...(query.tenantSlug === null ? {} : { tenant_slug: query.tenantSlug }),
    // The maximum the operation allows. A day grouping needs 31 and a model
    // grouping can need dozens; asking for the ceiling is what keeps
    // `groups_omitted` at zero, so the parts of a merged answer still add to
    // its whole.
    limit: MAX_LIST_LIMIT,
    sort: "cost_desc",
  };
}

/**
 * The report, in whichever of its two arms answered.
 *
 * A 202 is not a failure and not an empty result: it means the rollup does not
 * cover the scope, a job is filling it, and the previous answer is beside it.
 * The status picks the schema here so no screen has to guess from the shape of
 * a body.
 */
async function fetchUsageCost(
  query: UsageCostQuery,
  signal: AbortSignal,
): Promise<UsageCostAnswer> {
  const path = "/reports/usage-cost";
  const { status, body } = await apiGetRaw(path, { params: usageCostParams(query), signal });
  if (status === 202) {
    return { kind: "pending", pending: lenientParse(UsageCostPending, body, path) };
  }
  return { kind: "ready", report: lenientParse(UsageCostResponse, body, path) };
}

/**
 * One query per part of the scope.
 *
 * `useQueries` rather than a hook in a loop: the parts are a fixed-shape list
 * the screen merges, and the number of them changes with the chips.
 */
export function useUsageCost(
  queries: readonly UsageCostQuery[],
): UseQueryResult<UsageCostAnswer>[] {
  return useQueries({
    queries: queries.map((query) => ({
      queryKey: ["report", "usage-cost", usageCostParams(query)],
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchUsageCost(query, signal),
      placeholderData: (previous: UsageCostAnswer | undefined) => previous,
    })),
  });
}

/**
 * A rollup refresh, polled while it runs.
 *
 * The job carries no result: its completion means the rollup for that scope is
 * fresh, and the caller reads the report again. `refetchInterval` stops on its
 * own when the job reaches a terminal status, so a finished job is not polled
 * for as long as the screen stays open.
 */
export function useUsageJob(jobId: string | null): UseQueryResult<UsageJob> {
  return useQuery({
    queryKey: ["usage-job", jobId],
    queryFn: ({ signal }) => apiGet(UsageJob, `/reports/usage-jobs/${jobId ?? ""}`, { signal }),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "queued" || status === "running" ? USAGE_JOB_POLL_MS : false;
    },
    staleTime: 0,
  });
}

export const USAGE_JOB_POLL_MS = 2_000;

export interface UsageTrendQuery {
  readonly periodFrom: string;
  readonly periodTo: string;
  readonly deploymentSlug: string | null;
  readonly tenantSlugs: readonly string[];
  readonly groupBy: UsageTrendGroupBy;
  readonly limit: number;
  /** What every figure comes back in: the reader's report currency. */
  readonly currency: Currency;
}

/** Months side by side. One read per deployment, because the operation takes one. */
export function useUsageTrend(
  queries: readonly UsageTrendQuery[],
): UseQueryResult<UsageTrendResponse>[] {
  return useQueries({
    queries: queries.map((query) => {
      const params: QueryParams = {
        period_from: query.periodFrom,
        period_to: query.periodTo,
        ...(query.deploymentSlug === null ? {} : { deployment_slug: query.deploymentSlug }),
        tenant_slug: [...query.tenantSlugs],
        group_by: query.groupBy,
        limit: query.limit,
        currency: query.currency,
      };
      return {
        queryKey: ["report", "usage-trend", params],
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          apiGet(UsageTrendResponse, "/reports/usage-trend", { params, signal }),
        placeholderData: (previous: UsageTrendResponse | undefined) => previous,
      };
    }),
  });
}

// ---------------------------------------------------------------------------
// The rate card: one of the two arms that price a usage fact.
//
// The usage-cost hooks above say what a month cost; this says how a unit is
// priced when no provider figure covers it. Read-only: every write here is a
// CLI or an agent action today (`crm provider-rates set-provider-rate`), so
// this hook makes the rule visible without offering a half-built editor over a
// rule that bills money.
// ---------------------------------------------------------------------------

const ProviderRateListEnvelope = listEnvelope(ProviderRate);

/**
 * Every rate period, newest first within a key.
 *
 * Unpaged at the API's ceiling, because the screen groups the rows into ONE
 * row per key and hangs the rest under it as history — a page boundary drawn
 * through a key would show a price with the period that superseded it missing,
 * which is the one thing the card exists to show. `total` comes back in the
 * envelope, so the screen can say so when the ceiling actually cut something.
 */
export function useProviderRates(): UseQueryResult<ListEnvelope<ProviderRate>> {
  const params: QueryParams = {
    sort: ["provider:asc", "operation:asc", "effective_from:desc"],
    limit: MAX_LIST_LIMIT,
  };
  return useQuery({
    queryKey: ["provider-rates", params],
    queryFn: ({ signal }) =>
      apiGet(ProviderRateListEnvelope, "/provider-rates", { params, signal }),
  });
}

// ---------------------------------------------------------------------------
// What a derived figure is made of
//
// A fact-priced period is a sum the report already carries as ONE number. These
// read the same month day by day, so a screen can show that it is still adding
// up rather than only assert it — and can say how many facts the month could
// not price, which is the count a person is asked to accept.
//
// Two reads deep, and both are shared with the screens that already make them:
// a deal's tenant bindings say which (deployment, tenant) pairs its spending
// belongs to, and the usage-cost report answers one pair at a time. The keys
// are built the same way `useTenantBindings` and `useUsageCost` build theirs,
// so nothing here costs a second request when the same scope is already open.
// ---------------------------------------------------------------------------

/** One derived figure, named by the deal it belongs to and the month it is for. */
export interface DerivedGrowthQuery {
  readonly dealId: string;
  readonly period: string;
  /** The deal's currency, which the line's figure, and so its growth, is in. */
  readonly currency: Currency;
}

export interface DerivedGrowthDay {
  readonly day: string;
  readonly costMinor: number;
}

export interface DerivedGrowth {
  /** The month so far, one entry per day the rollup holds, oldest first. */
  readonly days: readonly DerivedGrowthDay[];
  /** Rates the month is still missing — every one of them a fact it could not price. */
  readonly rateGapCount: number;
  /** How many facts those gaps cover. The figure the issue dialog states. */
  readonly unpricedFacts: number;
}

/** The key both halves of this hook agree on. */
export function derivedGrowthKey(dealId: string, period: string, currency: Currency): string {
  return `${dealId}:${period}:${currency}`;
}

/**
 * A binding covers a month when it started on or before it and had not ended
 * before it. Compared at MONTH granularity, which is the granularity a bill
 * has: a binding that ended mid-month still earned part of that month's spend.
 */
function bindingCovers(binding: TenantBinding, period: string): boolean {
  if (binding.effective_from.slice(0, 7) > period) return false;
  return binding.effective_to === null || binding.effective_to.slice(0, 7) >= period;
}

export function useDerivedGrowth(
  queries: readonly DerivedGrowthQuery[],
): ReadonlyMap<string, DerivedGrowth> {
  const dealIds = [...new Set(queries.map((query) => query.dealId))].sort();
  const bindingResults = useQueries({
    queries: dealIds.map((dealId) => {
      const params: QueryParams = { deal_id: dealId, limit: MAX_LIST_LIMIT };
      return {
        // The same key `useTenantBindings` builds, so a deal screen and this
        // share one entry rather than reading the same list twice.
        queryKey: ["tenant-bindings", params],
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          apiGet(TenantBindingListEnvelope, "/tenant-bindings", { params, signal }),
      };
    }),
  });

  const bindingsByDeal = new Map<string, readonly TenantBinding[]>();
  dealIds.forEach((dealId, index) => {
    bindingsByDeal.set(dealId, bindingResults[index]?.data?.items ?? []);
  });

  // One usage-cost read per (deployment, tenant, month) the whole set needs,
  // never one per line: two recharges on one deal's month ask the same question.
  const scopes = new Map<string, UsageCostQuery>();
  for (const query of queries) {
    for (const binding of bindingsByDeal.get(query.dealId) ?? []) {
      if (!bindingCovers(binding, query.period)) continue;
      scopes.set(
        `${binding.deployment_slug}:${binding.tenant_slug}:${query.period}:${query.currency}`,
        {
          period: query.period,
          groupBy: "day",
          deploymentSlug: binding.deployment_slug,
          tenantSlug: binding.tenant_slug,
          // The deal's own currency: the growth is drawn under the line's figure,
          // in the line's money. It used to come back in USD and be labelled CAD.
          currency: query.currency,
        },
      );
    }
  }
  const scopeKeys = [...scopes.keys()].sort();
  const costResults = useUsageCost(scopeKeys.map((key) => scopes.get(key)).filter(isPresent));

  const reportByScope = new Map<string, UsageCostResponse>();
  scopeKeys.forEach((key, index) => {
    const answer = costResults[index]?.data;
    if (answer === undefined) return;
    // A 202 means the rollup is being filled. The previous answer beside it is
    // still the best picture of the month, and a blank line would read as a
    // month that has not started.
    const report = answer.kind === "ready" ? answer.report : answer.pending.stale_result;
    if (report !== null) reportByScope.set(key, report);
  });

  const growth = new Map<string, DerivedGrowth>();
  for (const query of queries) {
    const byDay = new Map<string, number>();
    let rateGapCount = 0;
    let unpricedFacts = 0;
    for (const binding of bindingsByDeal.get(query.dealId) ?? []) {
      if (!bindingCovers(binding, query.period)) continue;
      const report = reportByScope.get(
        `${binding.deployment_slug}:${binding.tenant_slug}:${query.period}:${query.currency}`,
      );
      if (report === undefined) continue;
      for (const group of report.groups) {
        byDay.set(group.key, (byDay.get(group.key) ?? 0) + group.cost_minor);
      }
      for (const gap of report.rate_gaps) {
        if (gap.resolved_at !== null) continue;
        rateGapCount += 1;
        unpricedFacts += gap.facts;
      }
    }
    growth.set(derivedGrowthKey(query.dealId, query.period, query.currency), {
      days: [...byDay.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([day, costMinor]) => ({ day, costMinor })),
      rateGapCount,
      unpricedFacts,
    });
  }
  return growth;
}

// ---------------------------------------------------------------------------
// Notes, and finding things
// ---------------------------------------------------------------------------

/**
 * One deal's notes, newest meeting first. The list carries no bodies — read a
 * note for its text.
 */
export function useDealNotes(dealId: string): UseQueryResult<ListEnvelope<NoteListItem>> {
  const params: QueryParams = { deal_id: dealId, limit: 100 };
  return useQuery({
    queryKey: ["notes", params],
    queryFn: ({ signal }) => apiGet(NoteListEnvelope, "/notes", { params, signal }),
  });
}

/**
 * The notes no deal has been determined for — the inbox.
 *
 * It is a query the app asks on its own rather than one a person has to go
 * looking for: an unfiled note is the one nobody has read, and the whole reason
 * `deal_id` is nullable is that dropping it would have been worse.
 */
export function useUnfiledNotes(): UseQueryResult<ListEnvelope<NoteListItem>> {
  const params: QueryParams = { unfiled: true, limit: 100 };
  return useQuery({
    queryKey: ["notes", params],
    queryFn: ({ signal }) => apiGet(NoteListEnvelope, "/notes", { params, signal }),
  });
}

export function useNotes(params: QueryParams): UseQueryResult<ListEnvelope<NoteListItem>> {
  return useQuery({
    queryKey: ["notes", params],
    queryFn: ({ signal }) => apiGet(NoteListEnvelope, "/notes", { params, signal }),
  });
}

/** One note in full, which is the only read that carries its body. */
export function useNote(noteId: string | null): UseQueryResult<Note> {
  return useQuery({
    queryKey: ["note", noteId],
    enabled: noteId !== null,
    queryFn: ({ signal }) => apiGet(Note, `/notes/${noteId ?? ""}`, { signal }),
  });
}

/**
 * The hybrid note search, answering with the passage that matched.
 *
 * Disabled on a blank query rather than asking for everything: a search box
 * that has not been typed in yet has not asked a question.
 */
export function useNoteSearch(query: string, dealId?: string): UseQueryResult<NoteSearchResults> {
  const trimmed = query.trim();
  const params: QueryParams = {
    q: trimmed,
    limit: 20,
    ...(dealId === undefined ? {} : { deal_id: dealId }),
  };
  return useQuery({
    queryKey: ["note-search", params],
    enabled: trimmed !== "",
    queryFn: ({ signal }) => apiGet(NoteSearchResults, "/notes/search", { params, signal }),
  });
}

/**
 * Global search: deals, partners, agreements, invoices, term lines, tenant
 * bindings and notes, grouped by kind.
 *
 * The caller DEBOUNCES the query it passes. The entity half is a handful of
 * indexed lookups, but the note half embeds the query — a call to a model
 * provider — and paying for that on every keystroke is both slow and wasteful.
 * The API skips it below three characters and says `semantic: false`, so a
 * short query is cheap either way.
 */
export function useGlobalSearch(query: string, limit = 5): UseQueryResult<SearchResults> {
  const trimmed = query.trim();
  const params: QueryParams = { q: trimmed, limit };
  return useQuery({
    queryKey: ["search", params],
    enabled: trimmed !== "",
    queryFn: ({ signal }) => apiGet(SearchResults, "/search", { params, signal }),
    // A search answer is about as perishable as the notes under it, and a box
    // re-asking the same question while somebody arrows through the results
    // would flicker.
    staleTime: 30_000,
  });
}

/** `filter` with a type guard, so a sparse lookup narrows rather than casts. */
function isPresent<T>(value: T | undefined): value is T {
  return value !== undefined;
}
